import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Fingerprint, Package, Layers, Boxes, Database, ShieldCheck,
  BarChart3, FlaskConical, LogOut, Zap, ShieldAlert, Menu, X, CreditCard, Dna, Gift, AlertTriangle, UserCog, Users,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { accentColor, NeonButton } from "@/components/Holo";
import api from "@/lib/api";
import Overview from "@/pages/dashboard/Overview";
import VaultModule from "@/pages/dashboard/VaultModule";
import WorldForge from "@/pages/dashboard/WorldForge";
import GroupForge from "@/pages/dashboard/GroupForge";
import MemoryVault from "@/pages/dashboard/MemoryVault";
import CreditShield from "@/pages/dashboard/CreditShield";
import Analytics from "@/pages/dashboard/Analytics";
import AILab from "@/pages/dashboard/AILab";
import Billing from "@/pages/dashboard/Billing";
import CreditCenter from "@/pages/dashboard/CreditCenter";
import Notifications from "@/pages/dashboard/Notifications";
import Account from "@/pages/dashboard/Account";

const NAV = [
  { key: "overview", icon: LayoutGrid, label: "Command Center", accent: "turquoise" },
  { key: "identity", icon: Fingerprint, label: "Identity Matrix", accent: "pink" },
  { key: "brand", icon: Package, label: "Brand Core", accent: "turquoise" },
  { key: "scene", icon: Layers, label: "Scene Memory", accent: "green" },
  { key: "worldforge", icon: Boxes, label: "World Forge", accent: "purple" },
  { key: "groupforge", icon: Users, label: "Group Forge", accent: "yellow" },
  { key: "memory_vault", icon: Database, label: "Memory Vault", accent: "yellow" },
  { key: "credit_shield", icon: ShieldCheck, label: "Credit Shield", accent: "turquoise" },
  { key: "analytics", icon: BarChart3, label: "Analytics", accent: "pink" },
  { key: "ai_lab", icon: FlaskConical, label: "AI Laboratory", accent: "green" },
  { key: "billing", icon: CreditCard, label: "Billing & Plans", accent: "yellow" },
  { key: "account", icon: UserCog, label: "My Account", accent: "purple" },
];

export default function Dashboard() {
  const [active, setActive] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showCredit, setShowCredit] = useState(false);
  const [emptyPrompted, setEmptyPrompted] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const planMonthly = plans.find((p) => p.name === user?.plan)?.credits || 50;
  const lowThreshold = Math.max(10, Math.round(planMonthly * 0.2));
  const credits = user?.credits ?? 0;
  const isEmpty = credits <= 0;
  const isLow = credits > 0 && credits <= lowThreshold;
  const isTrial = user?.plan === "Starter" && (user?.purchased_credits || 0) > 0 && (user?.monthly_credits || 0) === 0;

  useEffect(() => {
    if (isEmpty && !emptyPrompted) { setShowCredit(true); setEmptyPrompted(true); }
  }, [isEmpty, emptyPrompted]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    setActive("billing");
    let attempts = 0;
    const clear = () => window.history.replaceState({}, "", "/app");
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(`Subscription active — ${data.plan} plan · credits added`);
          await refreshUser();
          clear();
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired"); clear(); return; }
      } catch (e) { console.debug("Checkout status poll retry:", e?.message); }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else { toast.message("Still processing — check Billing shortly."); clear(); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const render = () => {
    switch (active) {
      case "overview": return <Overview setActive={setActive} />;
      case "identity": return <VaultModule kind="character" title="Identity Matrix™" subtitle="Save & lock a character's memory" accent="pink"
        lockOptions={["Face", "Skin Tone", "Body Proportions", "Hair", "Makeup", "Expressions", "Personality"]}
        attrFields={["Eye shape", "Nose shape", "Lip shape", "Jawline", "Muscle mass", "Height", "Body measurements", "Voice style", "Walking style", "Energy level"]}
        attrLabel="Character DNA™" attrIcon={Dna} previewLabel="Preview From Profile™" masterLabel="Master Character" noun="identity" />;
      case "brand": return <VaultModule kind="product" title="Brand Core™" subtitle="Lock every detail of your product" accent="turquoise"
        lockOptions={["Product Dimensions", "Logos", "Packaging", "Colors", "Labels", "Fonts", "Materials", "Variants"]}
        attrFields={["Category", "Dimensions", "Packaging Shape", "Logo Placement", "Brand Colors", "Fonts / Typography", "Materials / Finishes", "Product Variants"]}
        attrLabel="Brand Spec™" attrIcon={Package} previewLabel="Preview From Product Profile™" masterLabel="Master Product" noun="product" />;
      case "scene": return <VaultModule kind="scene" title="Scene Memory™" subtitle="Preserve scene continuity" accent="green"
        lockOptions={["Lighting", "Camera Angle", "Environment", "Background Details", "Props", "Color Grading", "Weather", "Mood"]}
        attrFields={["Location / Environment", "Background Details", "Lighting Direction", "Lighting Intensity", "Time of Day", "Camera Angle", "Lens / Framing", "Color Grading", "Props / Furniture", "Weather / Atmosphere", "Character Placement", "Product Placement", "Mood / Visual Style"]}
        attrLabel="Scene Spec™" attrIcon={Layers} previewLabel="Preview From Scene Profile™" masterLabel="Master Scene" noun="scene" />;
      case "worldforge": return <WorldForge />;
      case "groupforge": return <GroupForge />;
      case "memory_vault": return <MemoryVault />;
      case "credit_shield": return <CreditShield />;
      case "analytics": return <Analytics />;
      case "ai_lab": return <AILab />;
      case "billing": return <Billing />;
      case "account": return <Account onNavigate={(k) => setActive({ forge: "worldforge", vault: "memory_vault" }[k] || k)} />;
      default: return null;
    }
  };

  const go = (k) => { setActive(k); setMobileOpen(false); };

  return (
    <div className="min-h-screen bg-[#050505] grain text-white flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 glass border-r border-white/10 flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <Logo variant="neon" className="h-7" />
          <button className="lg:hidden text-white/50" onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => {
            const c = accentColor(n.accent);
            const on = active === n.key;
            return (
              <button key={n.key} onClick={() => go(n.key)} data-testid={`nav-${n.key}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 font-mono-tech text-[11px] uppercase tracking-wider transition-colors"
                style={on ? { color: c.hex, background: `rgba(${c.rgb},0.08)`, borderLeft: `2px solid ${c.hex}` }
                  : { color: "rgba(255,255,255,0.55)", borderLeft: "2px solid transparent" }}>
                <n.icon size={16} strokeWidth={1.5} /> {n.label}
              </button>
            );
          })}
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin")} data-testid="nav-admin"
              className="w-full flex items-center gap-3 px-3 py-2.5 font-mono-tech text-[11px] uppercase tracking-wider text-neon-yellow/80 hover:text-neon-yellow transition-colors border-l-2 border-transparent">
              <ShieldAlert size={16} strokeWidth={1.5} /> Admin Panel
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={() => go("account")} data-testid="profile-avatar-btn"
            className="w-full flex items-center gap-3 mb-3 p-2 -m-2 hover:bg-white/[0.04] transition-colors group">
            <div className="w-9 h-9 border border-neon-purple/40 overflow-hidden bg-black/40 flex items-center justify-center shrink-0">
              {user?.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : <UserCog size={16} className="text-white/40" />}
            </div>
            <div className="min-w-0 text-left">
              <div className="font-mono-tech text-xs text-white/85 truncate group-hover:text-neon-purple transition-colors">{user?.name}</div>
              <div className="font-mono-tech text-[9px] text-white/35 uppercase tracking-widest">My Account</div>
            </div>
          </button>
          <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-widest">Plan · {user?.plan}</div>
          <div className="flex items-center gap-2 mt-1 text-neon-turquoise font-mono-tech text-sm"><Zap size={13} /> {user?.credits?.toLocaleString()} credits</div>
          <div className="font-mono-tech text-[9px] text-white/35 mt-1">Monthly {user?.monthly_credits ?? 0} · Purchased {user?.purchased_credits ?? 0}</div>
          <button onClick={() => setShowCredit(true)} data-testid="open-credit-center"
            className="mt-3 w-full flex items-center gap-2 justify-center font-mono-tech text-[10px] uppercase tracking-widest border border-neon-green/40 text-neon-green py-2 hover:bg-neon-green/10 transition-colors">
            <Zap size={12} /> Buy Credits
          </button>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 justify-center font-mono-tech text-[11px] uppercase tracking-widest border border-white/15 py-2 text-white/60 hover:border-neon-pink hover:text-neon-pink transition-colors">
            <LogOut size={13} /> Disconnect
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden h-16 flex items-center justify-between px-5 border-b border-white/10 glass sticky top-0 z-20">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></button>
          <Logo variant="neon" className="h-6" />
          <span className="font-mono-tech text-xs text-neon-turquoise flex items-center gap-1"><Zap size={12} />{user?.credits}</span>
        </div>
        <main className="p-6 sm:p-10 max-w-7xl mx-auto">
          {isTrial && !bannerDismissed && (
            <div className="mb-6 flex items-center justify-between gap-4 border border-neon-green/40 bg-neon-green/5 px-5 py-3" data-testid="welcome-banner">
              <div className="flex items-center gap-3 font-mono-tech text-sm text-neon-green">
                <Gift size={16} /> Try FUTRA Free — 50 Credits Included.
              </div>
              <button onClick={() => setBannerDismissed(true)} className="text-white/40 hover:text-white"><X size={15} /></button>
            </div>
          )}
          {isLow && (
            <div className="mb-6 flex items-center justify-between gap-4 border border-neon-yellow/40 bg-neon-yellow/5 px-5 py-3" data-testid="low-credit-banner">
              <div className="flex items-center gap-3 font-mono-tech text-sm text-neon-yellow">
                <AlertTriangle size={16} /> Low credits — {user?.credits?.toLocaleString()} remaining. Recharge to keep creating.
              </div>
              <NeonButton accent="yellow" onClick={() => setShowCredit(true)} data-testid="low-banner-recharge"><Zap size={13} /> Recharge</NeonButton>
            </div>
          )}
          {render()}
        </main>
      </div>

      {showCredit && <CreditCenter onClose={() => setShowCredit(false)} onGoPlans={() => { setShowCredit(false); setActive("billing"); }} />}
      <Notifications />
    </div>
  );
}
